import {NodeElement, NodeElementOptions, StyleObject} from "./NodeElement";
import {PanelElement} from "./PanelElement";
import {IFrameMessages} from "../../messaging/IFrameMessages";
import {GATE_MOUNT_TYPE} from "../../Constants";
import {bodyScrollLock} from "../../utils/ScrimHelpers.js";

export interface InteractiveHandlers {
    remove: () => void;
    updatePanel: (panelOptions: Partial<NodeElementOptions>) => void;
}

export const GateStyle = {
    gate: {
        pointerEvents: "initial",
        height: "100%",
        display: "flex"
    },
};

export class GateElement extends NodeElement {
    styleSheet = GateStyle;
    panel: PanelElement;
    parentNode: HTMLElement;
    initialParentStyle: Record<string, string>;
    // Gates that cover the viewport set this; the lockBodyScroll option overrides it either way.
    locksBodyScrollByDefault = false;

    constructor(options: NodeElementOptions, panel: PanelElement) {
        super(options);
        this.panel = panel;
    }

    shouldLockBodyScroll(): boolean {
        return this.options.lockBodyScroll ?? this.locksBodyScrollByDefault;
    }

    refreshStyle(): void {
        super.refreshStyle();
        this.setStyle(this.styleSheet.gate);
        Object.assign(this.parentNode.style, this.computeParentStyle());
        this.panel.updateOptions({style: this.getPanelStyle()});
        this.setStyle(this.options.customStyle ?? {});
    }

    getPanelStyle(): StyleObject {
        return {
            maxWidth: "100%",
            minWidth: "100%",
            border: 0,
            height: 0,
        };
    }

    computeParentStyle(): StyleObject {
        return {};
    }

    mount(parentNode: HTMLElement) {
        this.initialParentStyle = {};
        for (const key of Array.from(parentNode.style)) {
            this.initialParentStyle[key] = parentNode.style.getPropertyValue(key);
        }

        this.parentNode = parentNode;
        if (this.options.mountType === GATE_MOUNT_TYPE.replaceChildren) {
            parentNode.innerHTML = "";
            super.mount(parentNode, 0);
        } else if (this.options.mountType === GATE_MOUNT_TYPE.firstChild) {
            super.mount(parentNode, 0);
        } else if (this.options.mountType === GATE_MOUNT_TYPE.lastChild) {
            super.mount(parentNode, parentNode.childNodes.length);
        }

        if (this.options.gateType) {
            this.node.classList.add("blink-" + this.options.gateType);
        }

        this.panel.mount(this.node);

        if (this.shouldLockBodyScroll()) {
            // Our own teardown releases the lock, since the handle is a RemoveHandle. The liveness check is
            // the backstop for the SPA-nav case, where the gate node is torn out of the DOM by the merchant's
            // router and destroyNode() never runs.
            this.addCleanupJob(bodyScrollLock.acquire(() => !!this.node && document.contains(this.node)));
        }
    }

    destroyNode(): void {
        this.panel.destroyNode();
        const revertedParentStyle: Record<string, string> = {};
        for (const key of Object.keys(this.computeParentStyle())) {
            revertedParentStyle[key] = "";
        }
        Object.assign(this.parentNode.style, revertedParentStyle, this.initialParentStyle);
        super.destroyNode();
    }

    getInteractiveHandlers(): InteractiveHandlers {
        return {
            remove: () => {
                if (!this.node) {
                    console.error("Panel has already been destroyed.");
                    return;
                }
                this.destroyNode();
            },
            updatePanel: (panelOptions: Partial<NodeElementOptions>) => {
                if (!this.panel) {
                    console.error("Panel has already been destroyed.");
                    return;
                }
                this.panel.updateOptions(panelOptions);
            }
        }
    }

    onMount(): void {
        super.onMount();
        this.panel.sizeChange.addListener(() => this.refreshStyle());
        this.panel.addSdkListener(IFrameMessages.DESTROY_PANEL, () => {
            this.destroyNode();
        });

        this.attachEventListener(document, "visibilitychange", () => {
            if (document.visibilityState === "visible") {
                this.refreshStyle();
            }
        });
    }
}
